import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronRight } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, ScoreRing, Spinner, Chip } from "../components/ui";

const GROUP_LABELS = {
  formation: "Business Formation",
  presence: "Business Presence",
  banking: "Banking",
  compliance: "Compliance",
  credit: "Credit Profile",
  planning: "Planning",
};

export default function BusinessBuilder() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/business/checklist").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    setSaving(item.key);
    const next = !item.done;
    setData((d) => ({
      ...d,
      items: d.items.map((x) => (x.key === item.key ? { ...x, done: next } : x)),
    }));
    try {
      const { data: res } = await api.put(`/business/checklist/${item.key}`, { done: next, value: item.value || "" });
      setData((d) => ({ ...d, completion: res.completion, done_count: d.items.filter((x) => x.done).length }));
      if (next) toast.success(`${item.label} completed`);
    } catch {
      toast.error("Could not save");
      load();
    } finally {
      setSaving(null);
    }
  };

  const saveValue = async (item, value) => {
    setData((d) => ({ ...d, items: d.items.map((x) => (x.key === item.key ? { ...x, value } : x)) }));
    try {
      await api.put(`/business/checklist/${item.key}`, { done: item.done, value });
    } catch (e) { console.error("checklist value save failed", e); }
  };

  if (!data) return <Spinner />;

  const groups = {};
  data.items.forEach((it) => { (groups[it.group] = groups[it.group] || []).push(it); });

  return (
    <div className="space-y-6">
      <PageHeader
        label="Flagship Feature"
        title="Business Credit Builder"
        subtitle="Your guided path from starting a business all the way to becoming funding ready."
      />

      <GlassCard className="p-6 flex flex-col sm:flex-row items-center gap-6">
        <ScoreRing value={data.completion} label="Complete" size={132} />
        <div className="flex-1 w-full">
          <div className="flex items-baseline justify-between">
            <h3 className="cc-heading text-lg font-500 text-white">Foundation Progress</h3>
            <span className="text-slate-400 text-sm">{data.done_count} / {data.total} complete</span>
          </div>
          <p className="text-slate-400 text-sm mt-2">
            Complete each step to strengthen your business foundation and improve your funding readiness score.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.keys(groups).map((g) => {
              const done = groups[g].filter((x) => x.done).length;
              return <Chip key={g} tone={done === groups[g].length ? "green" : "default"}>{GROUP_LABELS[g] || g}: {done}/{groups[g].length}</Chip>;
            })}
          </div>
        </div>
      </GlassCard>

      <div className="grid lg:grid-cols-2 gap-5">
        {Object.entries(groups).map(([g, items]) => (
          <GlassCard key={g} className="p-6">
            <h3 className="cc-heading text-base font-500 text-white mb-4 flex items-center gap-2">
              <ChevronRight size={16} className="text-blue-400" /> {GROUP_LABELS[g] || g}
            </h3>
            <div className="space-y-2">
              {items.map((item) => (
                <motion.div key={item.key} layout
                  className={`rounded-xl border px-3 py-2.5 transition-colors ${item.done ? "border-blue-500/30 bg-blue-500/[0.06]" : "border-white/[0.05] cc-surface"}`}>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggle(item)}
                      disabled={saving === item.key}
                      data-testid={`checklist-${item.key}`}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${item.done ? "cc-gradient border-transparent" : "border-white/20"}`}
                      aria-label={`Toggle ${item.label}`}
                    >
                      {item.done && <Check size={13} className="text-white" />}
                    </button>
                    <span className={`text-sm flex-1 ${item.done ? "text-white" : "text-slate-300"}`}>{item.label}</span>
                  </div>
                  {item.done && (
                    <input
                      defaultValue={item.value}
                      onBlur={(e) => saveValue(item, e.target.value)}
                      placeholder="Add a note or reference (optional)"
                      className="cc-input mt-2 w-full rounded-lg px-3 py-1.5 text-xs"
                      data-testid={`checklist-note-${item.key}`}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}
